import type { ActiveRoomSnapshot } from "@topzle/types";

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === "object" ? (value as UnknownRecord) : null;
}

function stringAt(value: unknown, key: string): string | null {
  const item = record(value)?.[key];
  return typeof item === "string" ? item : null;
}

function indexedPlayer(state: UnknownRecord, indexKey: string): string | null {
  const index = state[indexKey];
  const players = state.players;
  if (!Number.isInteger(index) || !Array.isArray(players)) return null;
  const player = players[index as number];
  return typeof player === "string" ? player : stringAt(player, "id");
}

export function roomTurnParticipantId(gameId: string | null, gameState: unknown): string | null {
  const state = record(gameState);
  if (!gameId || !state) return null;
  if (gameId === "tic-tac-toe" && state.status === "playing")
    return stringAt(state.players, String(state.turn));
  if (gameId === "chess" && state.status === "playing") {
    const activeColor = String(state.fen ?? "").split(" ")[1] === "b" ? "black" : "white";
    return stringAt(state.players, activeColor);
  }
  if (gameId === "ludo" && state.phase !== "finished") return indexedPlayer(state, "turnIndex");
  if (gameId === "connect-four" && state.status === "playing")
    return stringAt(state.players, String(state.turn));
  if (gameId === "battleship" && state.phase === "playing")
    return indexedPlayer(state, "turnIndex");
  if (gameId === "draw-guess" && state.phase === "drawing") return stringAt(state, "drawerId");
  if (gameId === "snakes-ladders" && state.phase === "playing")
    return indexedPlayer(state, "turnIndex");
  return null;
}

export function roomGameParticipantIds(gameId: string | null, gameState: unknown): string[] {
  const state = record(gameState);
  if (!gameId || !state) return [];
  const players = state.players;
  const playerRecord = record(players);
  const values: Array<string | null> =
    Array.isArray(players) && ["ludo", "snakes-ladders"].includes(gameId)
      ? players.map((player) => stringAt(player, "id"))
      : Array.isArray(players)
        ? players.filter((player): player is string => typeof player === "string")
        : playerRecord
          ? Object.values(playerRecord).filter(
              (player): player is string => typeof player === "string",
            )
          : [];
  if (gameId === "draw-guess" && typeof state.drawerId === "string") values.push(state.drawerId);
  return [...new Set(values.filter((player): player is string => Boolean(player)))];
}

export function shouldNotifyRoomParticipant(
  snapshot: ActiveRoomSnapshot | null | undefined,
  participantId: string,
): boolean {
  if (!snapshot || !participantId) return false;
  const gamePlayers = roomGameParticipantIds(snapshot.currentGameId, snapshot.gameState);
  if (gamePlayers.length > 0) {
    const viewerIsPlaying = gamePlayers.includes(participantId);
    const realPlayersArePlaying = gamePlayers.filter((id) => !id.startsWith("ai:")).length >= 2;
    return viewerIsPlaying || realPlayersArePlaying;
  }
  const viewer = snapshot.participants.find((participant) => participant.id === participantId);
  return Boolean(viewer && viewer.role !== "spectator" && viewer.role !== "ai");
}
